import React, { useState, useEffect } from "react"
import { Col, Row, Form, Table } from "react-bootstrap"
import { useNavigate, useParams } from "react-router-dom"
import { DatePicker } from "antd"
import moment from "moment"
import toast from "react-hot-toast"
import Navbar from "../../Components/Navbar/Navbar"
import { numberFormat, toastStyle } from "../../Helpers/Utils/Common"
import {
  getStatementOfAccount,
  updateInvoiceDescription,
  updatePaymentDescription,
} from "../../Helpers/apiCalls/StatementOfAccount/StatementOfAccountApi"
import "../Expenses/PurchaseOrders/PurchaseOrders.css"
import "./StatementOfAccount.css"

const { RangePicker } = DatePicker

export default function StatementOfAccountDetail({ edit }) {
  const { id } = useParams()
  const navigate = useNavigate()

  const [inactive, setInactive] = useState(true)
  const [range, setRange] = useState({ from: null, to: null })
  const [saving, setSaving] = useState(false)
  const [changedIndexes, setChangedIndexes] = useState(new Set())
  const [soa, setSoa] = useState({
    client_name: "",
    project_name: "",
    address: "",
    beginning_balance: 0,
    total_billings: 0,
    total_payment: 0,
    ending_balance: 0,
    account_current_balance: 0,
    notes: "",
    items: [],
  })

  useEffect(() => {
    fetchStatementOfAccount()
  }, [id, range.from, range.to])

  async function fetchStatementOfAccount() {
    const payload = { id }
    if (range.from && range.to) {
      payload.date_from = range.from.format("YYYY-MM-DD")
      payload.date_to = range.to.format("YYYY-MM-DD")
    }
    const response = await getStatementOfAccount(payload)
    if (response?.data) {
      const d = response.data.data ?? response.data ?? {}
      setSoa({
        client_name: d.client_name ?? "",
        project_name: d.project_name ?? "",
        address: d.address ?? "",
        beginning_balance: d.beginning_balance ?? 0,
        total_billings: d.total_billings ?? 0,
        total_payment: d.total_payment ?? 0,
        ending_balance: d.ending_balance ?? 0,
        account_current_balance: d.account_current_balance ?? 0,
        notes: d.notes ?? "",
        items: d.items ?? [],
      })
    }
  }

  const money = (v) => (v || v === 0 ? numberFormat(Number(v).toFixed(2)) : "")

  function updateItemDescription(index, value) {
    setSoa((prev) => {
      const items = [...prev.items]
      items[index] = { ...items[index], description: value }
      return { ...prev, items }
    })
    setChangedIndexes((prev) => new Set(prev).add(index))
  }

  function periodLabel() {
    if (range.from && range.to) {
      return `${range.from.format("MMMM DD, YYYY")} - ${range.to.format("MMMM DD, YYYY")}`
    }
    return "Whole duration"
  }

  async function handleSave() {
    if (changedIndexes.size === 0) {
      toast.success("No changes to save", { style: toastStyle() })
      return
    }

    setSaving(true)
    try {
      const results = await Promise.all(
        [...changedIndexes].map((index) => {
          const item = soa.items[index]
          return item.type === "invoice"
            ? updateInvoiceDescription(item.id, item.description)
            : updatePaymentDescription(item.id, item.description)
        })
      )

      if (results.every((r) => r.status === 200)) {
        toast.success("Statement of Account Saved Successfully", {
          style: toastStyle(),
        })
        setChangedIndexes(new Set())
      } else {
        toast.error("Error Saving Statement of Account", {
          style: toastStyle(),
        })
      }
    } finally {
      setSaving(false)
    }
  }

  function handleGenerate() {
    navigate(`/statement-of-account/print/${id}`, {
      state: {
        soa,
        from: range.from ? range.from.format("YYYY-MM-DD") : null,
        to: range.to ? range.to.format("YYYY-MM-DD") : null,
      },
    })
  }

  return (
    <div>
      <div className="page">
        <Navbar
          onCollapse={(inactive) => setInactive(inactive)}
          active={"FINANCIAL REPORT"}
        />
      </div>

      <div className={`manager-container ${inactive ? "inactive" : "active"}`}>
        <div className="d-flex justify-content-between align-items-start">
          <h1 className="page-title mb-4">
            {edit ? "Edit Statement of Account" : "Statement of Account"}
          </h1>
          <div className="review-po mt-2">
            <span className="pe-3">SOA NO.</span>
            <span>{id}</span>
          </div>
        </div>

        <div className="review-form mb-3">
          <Row className="review-container py-3">
            <Row>
              <Col xs={3}>
                <span className="review-label">Client Name</span>
              </Col>
              <Col xs={3}>
                <span className="review-label">Project Name</span>
              </Col>
              <Col xs={6}>
                <span className="review-label">Address</span>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={3}>
                <span className="review-data">{soa.client_name}</span>
              </Col>
              <Col xs={3}>
                <span className="review-data">{soa.project_name}</span>
              </Col>
              <Col xs={6}>
                <span className="review-data">{soa.address}</span>
              </Col>
            </Row>
          </Row>

          {/* Period / statement date */}
          <Row className="review-container py-3">
            <Row>
              <Col xs={6}>
                <span className="review-label">Period</span>
              </Col>
              <Col xs={6}>
                <span className="review-label">Statement Date</span>
              </Col>
            </Row>
            <Row className="mt-2 align-items-center">
              <Col xs={6}>
                {edit ? (
                  <RangePicker
                    className="nc-modal-custom-text-new-datepicker"
                    style={{ maxWidth: "320px" }}
                    value={[range.from, range.to]}
                    popupStyle={{ zIndex: 2000 }}
                    onChange={(v) =>
                      setRange({ from: v?.[0] || null, to: v?.[1] || null })
                    }
                  />
                ) : (
                  <span className="review-data">{periodLabel()}</span>
                )}
              </Col>
              <Col xs={6}>
                <span className="review-data">
                  {moment().format("MMMM DD, YYYY")}
                </span>
              </Col>
            </Row>
          </Row>

          {/* Summary */}
          <Row className="review-container py-3">
            <Row>
              <Col xs={3}>
                <span className="review-label">Beginning Balance</span>
              </Col>
              <Col xs={3}>
                <span className="review-label">Total Billings</span>
              </Col>
              <Col xs={3}>
                <span className="review-label">Total Payment</span>
              </Col>
              <Col xs={3}>
                <span className="review-label">Ending Balance</span>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col xs={3}>
                <span className="review-data">{money(soa.beginning_balance)}</span>
              </Col>
              <Col xs={3}>
                <span className="review-data">{money(soa.total_billings)}</span>
              </Col>
              <Col xs={3}>
                <span className="review-data">{money(soa.total_payment)}</span>
              </Col>
              <Col xs={3}>
                <span className="review-data">{money(soa.ending_balance)}</span>
              </Col>
            </Row>
          </Row>

          {/* Transactions */}
          <Row className="review-container py-3">
            <div className="d-flex flex-column">
              <span className="review-label mb-3">Transactions</span>
              <div className="review-purchased-items">
                <Table hover className="align-middle soa-tx-table">
                  <colgroup>
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "35%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "13%" }} />
                    <col style={{ width: "14%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Billing No.</th>
                      <th>Description</th>
                      <th className="text-end">Debit (₱)</th>
                      <th className="text-end">Credit (₱)</th>
                      <th className="text-end">Balance (₱)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soa.items.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-3">
                          No transactions.
                        </td>
                      </tr>
                    ) : (
                      soa.items.map((t, i) => (
                        <tr key={i} className="align-middle">
                          <td>{t.date ? moment(t.date).format("D MMM YYYY") : ""}</td>
                          <td>{t.type === "invoice" ? t.billing_no : ""}</td>
                          <td>
                            {edit ? (
                              <Form.Control
                                type="text"
                                className="nc-modal-custom-text"
                                value={t.description || ""}
                                onChange={(e) =>
                                  updateItemDescription(i, e.target.value)
                                }
                              />
                            ) : (
                              t.description
                            )}
                          </td>
                          <td className="text-end">{money(t.debit)}</td>
                          <td className="text-end">{money(t.credit)}</td>
                          <td className="text-end">{money(t.balance)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Account current balance */}
              <div className="d-flex justify-content-end align-items-center mt-3">
                <span className="review-label me-3 mb-0">
                  Account Current Balance
                </span>
                <span className="review-data" style={{ fontSize: "24px", fontWeight: "bold" }}>
                  {money(soa.account_current_balance)}
                </span>
              </div>
            </div>
          </Row>

          {/* Notes */}
          <Row className="review-container py-3">
            <Row>
              <Col>
                <span className="review-label">Notes</span>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                {edit ? (
                  <Form.Control
                    as="textarea"
                    rows={3}
                    className="nc-modal-custom-text"
                    placeholder="Add a note (optional)"
                    value={soa.notes}
                    onChange={(e) => setSoa((p) => ({ ...p, notes: e.target.value }))}
                  />
                ) : (
                  <span className="review-data">{soa.notes || "—"}</span>
                )}
              </Col>
            </Row>
          </Row>
        </div>

        {/* buttons */}
        <div className="d-flex justify-content-end my-4 pb-5">
          <button
            className="button-secondary me-3"
            onClick={() => navigate("/statement-of-account")}
          >
            Close
          </button>
          {edit && (
            <button
              className="button-secondary me-3"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          )}
          <button className="button-primary" onClick={handleGenerate}>
            Generate
          </button>
        </div>
      </div>
    </div>
  )
}